import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FileInfo, GALAXY_UPLOADER_SERVICE_TOKEN, GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { UploadService } from '../test-management-upload.service';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ChatMessage, ChatMessageRole, TestCase, TestType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { MatCheckboxModule } from '@angular/material/checkbox';

export interface RowError {
  rowIndex: number;
  message: string;
}

export interface TestUploadModalResult {
  cases: Partial<TestCase>[];
  removeExisting: boolean;
}

interface TestCaseJSON {
  id: string;
  name: string;
  chatHistory?: { role: string; content: string }[];
  callerInstructions?: string;
  expectation: string;
}

@Component({
  selector: 'ai-test-upload-modal',
  imports: [
    MatButtonModule,
    MatDialogModule,
    CommonModule,
    GalaxyPageModule,
    GalaxyStickyFooterModule,
    MatCardModule,
    ReactiveFormsModule,
    AiAssistantI18nModule,
    GalaxyFormFieldModule,
    GalaxyUploaderModule,
    FormsModule,
    MatCheckboxModule,
  ],

  providers: [UploadService, { provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: UploadService }],
  templateUrl: './test-upload-modal.component.html',
  styleUrl: './test-upload-modal.component.scss',
})
export class TestUploadModalComponent {
  testCases: Partial<TestCase>[] = [];
  errors: RowError[] = [];
  private readonly dialogRef = inject(MatDialogRef<TestUploadModalComponent, TestUploadModalResult>);
  private readonly translateService = inject(TranslateService);
  isVoice = false;
  removeExisting = false;

  async onFileSelected(event: FileInfo) {
    if (!event?.file) return;

    const file = await event?.file?.text();

    try {
      const testCases: TestCaseJSON[] = JSON.parse(file);
      this.testCases = testCases.map((testCase) => {
        const chatHistory: ChatMessage[] = (testCase.chatHistory || []).map((message) => ({
          role: stringToRole(message.role),
          content: message.content,
          contentParts: [],
          externalId: '',
        }));

        const tc: Partial<TestCase> = {
          id: testCase.id,
          name: testCase.name,
          chatHistory: chatHistory,
          callerInstructions: testCase.callerInstructions ?? '',
          expectation: testCase.expectation,
          testType: this.isVoice ? TestType.TEST_TYPE_VOICE : TestType.TEST_TYPE_CHAT,
          expectedToolCalls: [],
          agentArchitecture: 'UNSET',
          assistantKey: undefined,
          expectedCitations: [],
          isSuccessEvaluator: false,
          scenario: '',
        };
        return tc;
      });
      [this.testCases, this.errors] = validateTestCases(this.testCases, this.isVoice, this.translateService);
    } catch {
      this.testCases = [];
      this.errors = [
        { rowIndex: 0, message: this.translateService.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.INVALID_JSON') },
      ];
    }
  }

  downloadTemplate(): void {
    const jsonContent = this.isVoice
      ? `[{"name": "Schedule an Appointment", "callerInstructions": "Call and ask to schedule a haircut appointment for next Tuesday at 2pm", "expectation": "Should confirm the appointment details and book the time slot"}]`
      : `[{"name": "Delete a Task in Task Manager", "chatHistory": [{"role": "user", "content": "How do I delete a task in Task Manager?"}], "expectation": "Should instruct the user to navigate to the tasks tab. Click on the three dots next to the task. Hit the delete button"}]`;

    const blob = new Blob([jsonContent], { type: 'text/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = this.isVoice ? 'VoiceTestCaseTemplate.json' : 'ChatTestCaseTemplate.json';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.dialogRef.close({ cases: this.testCases, removeExisting: this.removeExisting });
  }
}

function stringToRole(role: string): ChatMessageRole {
  switch (role) {
    case 'user':
      return ChatMessageRole.CHAT_MESSAGE_ROLE_USER;
    case 'assistant':
      return ChatMessageRole.CHAT_MESSAGE_ROLE_ASSISTANT;
    default:
      return ChatMessageRole.CHAT_MESSAGE_ROLE_INVALID;
  }
}

function validateTestCases(
  testCases: Partial<TestCase>[],
  isVoice: boolean,
  t: TranslateService,
): [Partial<TestCase>[], RowError[]] {
  const errors: RowError[] = [];
  const validTests: Partial<TestCase>[] = [];
  testCases.forEach((testCase, index) => {
    if (!testCase.name) {
      errors.push({ rowIndex: index, message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.NAME_REQUIRED') });
      return;
    }
    if (isVoice) {
      if (!testCase.callerInstructions) {
        errors.push({
          rowIndex: index,
          message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.CALLER_INSTRUCTIONS_REQUIRED'),
        });
        return;
      }
    } else {
      if (testCase.chatHistory?.length === 0) {
        errors.push({ rowIndex: index, message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.HISTORY_EMPTY') });
        return;
      }
      testCase.chatHistory?.forEach((message) => {
        if (!message.content) {
          errors.push({ rowIndex: index, message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.CONTENT_REQUIRED') });
          return;
        }
        if (message.role === ChatMessageRole.CHAT_MESSAGE_ROLE_INVALID) {
          errors.push({ rowIndex: index, message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.ROLE_INVALID') });
          return;
        }
      });
    }
    if (!testCase.expectation) {
      errors.push({
        rowIndex: index,
        message: t.instant('AI_ASSISTANT.TESTING.UPLOAD_MODAL.EXPECTATION_REQUIRED'),
      });
      return;
    }
    validTests.push(testCase);
  });
  return [validTests, errors];
}
